import { PATH_API } from "constants/api";
import {
  DATA_STORE,
  MapApiWithStoreName,
  StoreName,
} from "constants/serviceWorker";
import { iCachedItem } from "interfaces/models/serviceWorker";
import { getIndexedDb } from "utils/indexedDb";

function convertStoreNameToPathAPI(storeName: string) {
  const apiPath = Object.keys(MapApiWithStoreName).find((apiPath) => {
    const key = apiPath as keyof typeof MapApiWithStoreName;

    return MapApiWithStoreName[key] === storeName;
  });

  return apiPath || storeName;
}

function convertPathAPIToStoreName(pathAPI: string) {
  const apiPaths = Object.values(PATH_API);
  const matchPath = apiPaths
    .filter((path) => pathAPI.startsWith(path))
    .sort((a, b) => b.length - a.length)[0]; // match with longest string
  if (matchPath) {
    return MapApiWithStoreName[matchPath as keyof typeof MapApiWithStoreName];
  }

  return;
}

const removeItemAndRelative = async (args: {
  cachedItemId: string;
  storeNameOfRequest: string;
}) => {
  const { cachedItemId, storeNameOfRequest } = args;
  const indexedDb = await getIndexedDb();
  const cachedItems =
    (await indexedDb?.getAll(DATA_STORE, storeNameOfRequest)) || [];
  const promises = cachedItems.map((item) => {
    if (item.data?.id === cachedItemId) {
      return indexedDb.delete(item.id, DATA_STORE);
    }

    return Promise.resolve(true);
  });

  await Promise.all(promises);
  // clear logs and comments
  if (storeNameOfRequest === StoreName.TASKS) {
    // clear comments
    const cacheTaskComments = ((await indexedDb?.getAll(
      DATA_STORE,
      StoreName.TASK_COMMENTS
    )) || []) as iCachedItem[];
    const promises = cacheTaskComments.map((item) => {
      if (item.data?.taskId === cachedItemId) {
        return indexedDb.delete(item.id);
      }

      return Promise.resolve(true);
    });
    await Promise.all(promises);
  }
  // now we only care doc items not care  (offline mode not allow delete doc group and doc category)
  if (
    [StoreName.DOCUMENT_ITEMS, StoreName.SUB_ITEMS].includes(
      storeNameOfRequest as any
    )
  ) {
    const isStoreDocItem = storeNameOfRequest === StoreName.DOCUMENT_ITEMS;
    const setDeleteSubItemId: Set<string> = new Set([]);
    if (isStoreDocItem) {
      const cacheSubItems = ((await indexedDb?.getAll(
        DATA_STORE,
        StoreName.SUB_ITEMS
      )) || []) as iCachedItem[];
      const promiseDeleteSubItems = cacheSubItems.map((item) => {
        if (item.data?.itemId === cachedItemId) {
          setDeleteSubItemId.add(item.data.id);

          return indexedDb.delete(item.id);
        }

        return Promise.resolve(true);
      });
      await Promise.all(promiseDeleteSubItems);
    }
    const isStoreSubItem = storeNameOfRequest === StoreName.SUB_ITEMS;
    // clear comments
    const cacheLogs = ((await indexedDb?.getAll(
      DATA_STORE,
      StoreName.DATA_LOGS
    )) || []) as iCachedItem[];
    const promises = cacheLogs.map((item) => {
      if (
        (item.data?.itemId === cachedItemId && isStoreDocItem) ||
        (setDeleteSubItemId.has(item.data?.subItemId) && isStoreDocItem) ||
        (item.data?.subItemId === cachedItemId && isStoreSubItem)
      ) {
        return indexedDb.delete(item.id);
      }

      return Promise.resolve(true);
    });
    await Promise.all(promises);
    const cacheBlackBoards = ((await indexedDb?.getAll(
      DATA_STORE,
      StoreName.BLACKBOARDS
    )) || []) as iCachedItem[];

    const promiseBlackboards = cacheBlackBoards.map((item) => {
      if (
        (setDeleteSubItemId.has(item.data?.subItemId) && isStoreDocItem) ||
        (item.data?.subItemId === cachedItemId && isStoreSubItem)
      ) {
        return indexedDb.delete(item.id);
      }

      return Promise.resolve(true);
    });
    await Promise.all(promiseBlackboards);
  }
};

export const ServiceWorkerHelper = {
  convertStoreNameToPathAPI,
  convertPathAPIToStoreName,
  removeItemAndRelative,
};
